// Global Instructions Rule Applied!
//
// Deal Execution board orchestration: fetch → Redux, plus the two mutations
// the board makes — within-column reordering (pipeline_deals.sequence) and
// checklist answer toggling. Both update Redux optimistically and refetch on
// failure.
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  setExecutionBoard,
  updateExecutionBoardRow
} from '../../../state_manager/state_update_actions/deal_execution/index.ts'
import {
  DealExecutionBoardRecord,
  DealExecutionBoardRecordFields as F
} from '../../../state_manager/data_models/deal_execution_board_model.ts'
import {
  fetchExecutionBoard,
  patchPipelineDeal,
  setChecklistAnswer
} from '../services/dealExecutionService.ts'

export interface ExecutionBoardFilters {
  pipelineId?: string | null
  search?: string | null
}

export const useDealExecutionBoard = () => {
  const dispatch = useDispatch()
  const board = useSelector(
    (state: { dealExecution?: { board: DealExecutionBoardRecord[] } }) =>
      state.dealExecution?.board ?? []
  )
  const stale = useSelector(
    (state: { dealExecution?: { stale?: boolean } }) => state.dealExecution?.stale ?? false
  )
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<ExecutionBoardFilters>({})

  const refetch = useCallback(
    async (override?: ExecutionBoardFilters) => {
      const effective = override ?? filters
      setLoading(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const result = await fetchExecutionBoard(token, {
          pipelineId: effective.pipelineId,
          search: effective.search
        })
        dispatch(setExecutionBoard(result.data))
      } finally {
        setLoading(false)
      }
    },
    [dispatch, filters]
  )

  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pipelineId, filters.search])

  // Cross-module invalidation: creating a deal can auto-enroll it in
  // execution server-side, so the create path marks the board stale and this
  // refetch (with the board's own filters) picks the new card up without a
  // hard refresh. SET_EXECUTION_BOARD clears the flag.
  useEffect(() => {
    if (stale) refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stale])

  /**
   * Within-column reorder: persist the moved card's new sequence, plus the
   * neighbours the planner had to renumber when the drop landed on a tie.
   */
  const reorderCard = useCallback(
    async (
      pipelineDealsId: string,
      newSequence: number,
      renumbered: Array<{ id: string; sequence: number }> = []
    ) => {
      const writes = [{ id: pipelineDealsId, sequence: newSequence }, ...renumbered]
      writes.forEach(({ id, sequence }) =>
        dispatch(
          updateExecutionBoardRow(id, {
            [F.pipeline_deals__sequence]: sequence
          } as Partial<DealExecutionBoardRecord>)
        )
      )
      const token = getAccessToken(store.getState().auth)
      const results = await Promise.all(writes.map((write) => patchPipelineDeal(token, write)))
      if (results.some((result) => !result.success)) {
        await refetch()
      }
    },
    [dispatch, refetch]
  )

  /** Card checkbox toggle: optimistic checklist update, audited server-side. */
  const toggleChecklistItem = useCallback(
    async (row: DealExecutionBoardRecord, checklistItemId: string, answer: boolean) => {
      const updatedChecklist = (row[F.prog__checklist] || []).map((item) =>
        item.id === checklistItemId ? { ...item, answer } : item
      )
      dispatch(
        updateExecutionBoardRow(row[F.pipeline_deals__id], {
          [F.prog__checklist]: updatedChecklist
        } as Partial<DealExecutionBoardRecord>)
      )
      const token = getAccessToken(store.getState().auth)
      const result = await setChecklistAnswer(token, row[F.deals__id], checklistItemId, answer)
      if (result.error) {
        await refetch()
      }
    },
    [dispatch, refetch]
  )

  return {
    board,
    loading,
    filters,
    setFilters,
    refetch,
    reorderCard,
    toggleChecklistItem
  }
}
